import Destination from '@/components/Passenger/models/Destination';
import store from '@/store';
import { IEntity, IPropertyUpdate, IExcelRow, IEntityMapping, IPropertyMapping } from '@/typings/import';
import Passenger from '@/components/Passenger/models/Passenger';
import { EEntityImportState, EEntityMatchType } from '@/components/Importer/enums';
import PassengerMapping from './PassengerMapping';
import PropertyUpdateHelper from '../PropertyUpdateHelper';

export default class PassengerEntity implements IEntity<Passenger> {
  
  entity?: Passenger;
  matchedBy: EEntityMatchType;
  propertyUpdates: Array<IPropertyUpdate<Passenger>>;
  excelRowIndex: number;
  selected: boolean;
  importState: EEntityImportState;

  constructor(row: IExcelRow, index: number, mapping: IEntityMapping<Passenger>) {
    const propertyUpdates: Array<IPropertyUpdate<Passenger>> = mapping.props
      .filter(pm => {
        return pm && pm.propertyName && !pm.propertyName.startsWith(PassengerMapping.UTILITY_KEY)
      })
      .map(pm => {
        const propertyUpdate: IPropertyUpdate<Passenger> = {
          propertyName: pm.propertyName,
          propertyType: pm.propertyType,
          rowKey: pm.cell ? pm.cell : '',
          oldValue: null,
          newValue: PropertyUpdateHelper.getNewValueFromMapping(pm, row),
          doNotUpdate: false,
          overrideValue: null,
          isValid: () => false,
        };
        propertyUpdate.isValid = this.getIsValid(propertyUpdate, pm);
        return propertyUpdate;
      });
    
    const utilityUpdate = this.getUtilitiesPropertyUpdate(row, mapping);
    propertyUpdates.push(utilityUpdate);

    this.propertyUpdates = propertyUpdates;
    this.matchedBy = EEntityMatchType.Unknown;
    this.excelRowIndex = index;
    this.selected = false;
    this.importState = EEntityImportState.Mapped;
  }

  onMappingChanged(mapping: IEntityMapping<Passenger>, row: IExcelRow) {
    mapping.props.filter(pm => {
      return pm && pm.propertyName && !pm.propertyName.startsWith(PassengerMapping.UTILITY_KEY)
    }).forEach(pm => {
      const propertyUpdate = this.propertyUpdates.find(pu => pu.propertyName === pm.propertyName);
      if (propertyUpdate) {
        propertyUpdate.newValue = PropertyUpdateHelper.getNewValueFromMapping(pm, row);
      }
    });
    const utilityUpdateIndex =  this.propertyUpdates.findIndex(p => p.propertyName === 'utilities');
    if (utilityUpdateIndex > -1) {
      this.propertyUpdates.splice(utilityUpdateIndex, 1, this.getUtilitiesPropertyUpdate(row, mapping));
    }
  }

  isValid(): boolean {
    return this.propertyUpdates.every(pu => pu.isValid());
  }

  private getIsValid(propertyUpdate: IPropertyUpdate<any>, propertyMapping: IPropertyMapping) {
    
    if (!PropertyUpdateHelper.propertyMappingIsDefined(propertyMapping)) {
      return () => true;
    }

    // Always get the most up to date value via JS magic
    const getValue = () => PropertyUpdateHelper.getValue(propertyUpdate);
    
    switch (propertyUpdate.propertyName) {
      case 'name':
        return () => {
          return getValue().length > 3 && getValue().length <= 128 ? true : false
        };
      case 'destination':
        return (params: Destination[]) => {
          const value = getValue();
          return params.some(d => d.alias === value);
        }
      case 'homeAddress':
        return () => getValue() && getValue().length <= 512 ? true : false;
      case 'pickupAddress':
        return () => getValue() ? getValue().length <= 512 : true;
      case 'language':
        return () => getValue() ? getValue().length <= 128 : true;
      case 'note':
        return () => getValue() ? getValue().length <= 1024 : true;
      case 'timitNote':
        return () => getValue() ? getValue().length <= 2048 : true;
      case 'utilities':
        return () => getValue() ? getValue().length <= 32 : true;
      case 'email':
        return () => getValue() ? getValue().length <= 256 : true;
      case 'billingAddress':
        return () => getValue() ? getValue().length <= 512 : true;
      case 'billingPostalcode':
        return () => getValue() ? getValue().length <= 32 : true;
      case 'billingCity':
        return () => getValue() ? getValue().length <= 32 : true;
      case 'billingCity':
        return () => getValue() ? getValue().length <= 32 : true;
      default:
        return () => true;
    }
  }

  private getUtilitiesPropertyUpdate(row: IExcelRow, mapping: IEntityMapping<Passenger>) {
    const utilityUpdateNewValue = mapping.props
      .filter(p => p && p.propertyName && p.propertyName.includes(PassengerMapping.UTILITY_KEY))
      .map(p => {
        if (!p.cell) {
          return null;
        }
        const data = row.excelRowData[p.cell];
        const hasData = data ? data.trim() ? data : null : null;
        if (!hasData) {
          return null;
        }
        const nameParts = p.propertyName.split('-');
        if (nameParts.length !== 2) {
          return null;
        }
        return nameParts[1];
      })
      .filter(x => x)
      .join(',');

    const utilityUpdate: IPropertyUpdate<Passenger> = {
      propertyName: PassengerMapping.PROPERTY_MAPPING_UTILITIES,
      propertyType: 'string',
      rowKey: '',
      oldValue: null,
      newValue: utilityUpdateNewValue as unknown as object,
      doNotUpdate: false,
      overrideValue: null,
      isValid: () => false,
    }

    return utilityUpdate
  }
}
